import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";

import { useDispatch, useSelector } from "react-redux";
import { setDrawer } from "../../../redux-store/features/layoutSlice";
import {
  useAddSponsorUser,
  useGetParentsponsorDetails,
  useGetSponsorUserDetails,
  useUpdateSponsorUser,
} from "../../../custom-hooks/queries";
import NotificationModal from "../../../components/NotificationModal";
import { v4 as uuidv4 } from "uuid";
import {
  rightsCategories,
  rightsDescriptions,
  rightsList,
  roleDefinitions,
  CAN_GRANT_CHAT_DILIGENT_ACCESS_EMAILS,
  CAN_GRANT_CHAT_DILIGENT_ACCESS_DOMAIN,
  RIGHTS_DISPLAY_NAMES,
  ROLE_DISPLAY_NAMES,
  PERMISSION_CATEGORY_DISPLAY_NAMES,
} from "../../../utils/constants";
import { useUserRole } from "../../../utils/helperMethods";
import FormSkeletonLoader from "../../../components/FormSkeletonLoader";
import { isLoggedInSponsorTeamMember } from "../../../utils/teamMemberSelf";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import {
  getSponsorSelfProfileRedirectPath,
} from "../../../utils/selfProfileRedirect";
import { isVqaOnlyOrg, resolveUserHyperComplyOrg, useIsOrgVqaOnly } from "../../../utils/vqaOnlyOrg";
import { supportedEmailCharacters } from "../../../utils/emailSupportedCharacters";
import {
  DEFAULT_NEW_SPONSOR_SUBROLES,
  applySponsorPermissionsForRoles,
  isUserRoleSelectionSaveDisabled,
} from "../../../utils/userRolePermissionDefaults";

/** Reusable text input component */
const FormInput = ({
  label,
  name,
  register,
  errors,
  placeholder,
  numeric,
  type = "text",
  options,
  disabled = false,
  className = "",
}) => {
  if (type === "select") {
    return (
      <div className={`flex flex-col ${className}`}>
        <label className="block text-sm font-medium text-gray-900">
          {label}
        </label>
        <select
          {...register(name)}
          disabled={disabled}
          className="mt-1 ring-0 border-gray-300 rounded-md text-sm"
        >
          <option value="">-- Select --</option>
          {(options || []).map((opt) => (
            <option
              key={typeof opt === "object" ? opt.value || opt.sponsor_id : opt}
              value={
                typeof opt === "object" ? opt.value || opt.sponsor_id : opt
              }
            >
              {typeof opt === "object" ? opt.label || opt.sponsor_name : opt}
            </option>
          ))}
        </select>
        {errors[name] && (
          <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <label className="block text-sm font-medium text-gray-900">{label}</label>
      <input
        type={type}
        {...register(name)}
        placeholder={placeholder}
        disabled={disabled}
        onKeyPress={
          numeric
            ? (e) => {
                if (!/^[0-9]*$/.test(e.key)) e.preventDefault();
              }
            : undefined
        }
        className={`mt-1 w-full border-gray-300 rounded-md text-sm ${
          disabled ? "bg-gray-100" : ""
        }`}
      />
      {errors[name] && (
        <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
      )}
    </div>
  );
};

/** Reusable radio group component for contact preference */
const RadioGroup = ({ label, name, register, errors }) => (
  <div className="flex flex-col">
    <label className="block text-sm font-medium text-gray-900">{label}</label>
    <div className="flex items-center gap-6 mt-1">
      <label className="flex items-center">
        <input
          type="radio"
          {...register(name)}
          value="Mobile_Phone"
          className="mr-2"
        />
        Mobile Phone
      </label>
      <label className="flex items-center">
        <input
          type="radio"
          {...register(name)}
          value="Office_Phone"
          className="mr-2"
        />
        Office Phone
      </label>
    </div>
    {errors[name] && (
      <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
    )}
  </div>
);

// Role definitions with descriptions and default rights

const AddTeamMember = () => {
  // Helper to format phone number as 1-234-567-8900
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "");
    let formatted = "";
    if (digits.length > 0) {
      formatted += digits[0];
    }
    if (digits.length > 1) {
      formatted += "-";
      formatted += digits.slice(1, 4);
    }
    if (digits.length > 4) {
      formatted += "-";
      formatted += digits.slice(4, 7);
    }
    if (digits.length > 7) {
      formatted += "-";
      formatted += digits.slice(7, 11);
    }
    return formatted;
  };
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const { user: clerkUser } = useUser();
  const loggedInRole = useUserRole();

  // Add state to track if first load has completed
  const [firstLoadComplete, setFirstLoadComplete] = useState(false);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [isSuccess, setIsSuccess] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [userDataLoaded, setUserDataLoaded] = React.useState(false);
  const [refreshKey, setRefreshKey] = useState(Date.now());

  // Now useGetSponsorUserDetails can use refreshKey safely
  const {
    data: userDetails,
    isLoading: isLoadingUserDetails,
    refetch,
    isFetching,
  } = useGetSponsorUserDetails(userId, {
    enabled: !!userId,
    refetchOnWindowFocus: false,
    staleTime: 0,
    cacheTime: 0,
    retry: 3,
    queryKey: ["sponsorUserDetails", userId, refreshKey],
    onSuccess: (data) => {
      // Force a complete reset of the form whenever we get fresh data
      if (data) {
        setTimeout(() => {
          setUserDataLoaded(false); // Reset this flag to trigger the useEffect
          setFirstLoadComplete(true);
        }, 100);
      }
    },
  });

  const isEditingSelf =
    !!userId &&
    !!clerkUser?.id &&
    (String(userId) === String(clerkUser.id) ||
      isLoggedInSponsorTeamMember(userDetails, clerkUser.id));

  useEffect(() => {
    if (!isEditingSelf) return;
    const path = getSponsorSelfProfileRedirectPath(loggedInRole);
    if (path) navigate(path, { replace: true });
  }, [isEditingSelf, navigate, loggedInRole]);

  const { mutate: addSponsor } = useAddSponsorUser();
  const { mutate: updateSponsor } = useUpdateSponsorUser();

  const id = searchParams.get("id");
  const name = searchParams.get("name");
  const orgId = searchParams.get("orgId");

  // 14729: the sponsor portal cannot call the admin sponsor list (gateway 403), which used
  // to blank sponsor_name on every save. GET /Sponsors/Admin/{id} is allowed for sponsor users.
  const { data: sponsorDetails } = useGetParentsponsorDetails(id);
  // The page only serves the member's own org, so their profile answers until the details load.
  const ownOrgIsVqaOnly = useIsOrgVqaOnly();

  const decodeId = atob(orgId);

  const loggedInUserDetails = useSelector((state) => state.loggedInUser?.userDetails);
  const email = loggedInUserDetails?.email?.toLowerCase();
  const canGrantChatDiligentAccess = Boolean(
    email &&
      (CAN_GRANT_CHAT_DILIGENT_ACCESS_EMAILS.some((e) => e?.toLowerCase() === email) ||
        email.endsWith(CAN_GRANT_CHAT_DILIGENT_ACCESS_DOMAIN.toLowerCase()))
  );

  const schema = yup.object().shape({
    email: yup
      .string()
      .email("Enter a valid email")
      .test(supportedEmailCharacters)
      .required("Email is Required"),
    first_name: yup.string().required("First name is Required"),
    last_name: yup.string().required("Last name is Required"),
    job_title: yup
      .string()
      .nullable()
      .transform((value) => (value === "" || value === undefined ? null : value)),
    office_phone_number: yup
      .string()
      .matches(
        /^[\d]{1}-[\d]{3}-[\d]{3}-[\d]{4}$/,
        "Please enter a valid office phone number (e.g. 1-234-567-8900)"
      )
      .nullable()
      .transform((value) => (value === "" ? null : value)),
    phone_number: yup
      .string()
      .matches(
        /^[\d]{1}-[\d]{3}-[\d]{3}-[\d]{4}$/,
        "Please enter a valid mobile phone number (e.g. 1-234-567-8900)"
      )
      .nullable()
      .transform((value) => (value === "" ? null : value)),
    contact_preference: yup.string().required("Required"),
    role: yup.array().min(1, "At least one role must be selected"),
    is_active: yup.boolean().optional(),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onTouched",
    defaultValues: {
      contact_preference: "Mobile_Phone",
      role: DEFAULT_NEW_SPONSOR_SUBROLES,
      is_active: true,
      Vendors: true,
      job_title: "",
    },
  });

  const selectedRoles = watch("role") || [];

  // Load user data if in edit mode and data is available
  useEffect(() => {
    // Only load user data if we're in edit mode (userId exists)
    // AND we have user details AND the data hasn't been loaded yet
    if (userId && userDetails && (!userDataLoaded || !firstLoadComplete)) {
      // Step 0: Clear the entire form first
      reset({}, { keepDefaultValues: false });

      // Step 1: Set the basic user information
      reset({
        sponsor_id: userDetails.sponsor_id,
        email: userDetails.email,
        first_name: userDetails.first_name,
        last_name: userDetails.last_name,
        job_title: userDetails.job_title ?? userDetails.title ?? "",
        phone_number: userDetails.phone_number,
        office_phone_number: userDetails.office_phone_number || "",
        contact_preference: userDetails.contact_preference || "Mobile_Phone",
        is_active: userDetails.is_active !== false, // Default to true if not specified
        country: userDetails.country || "",
        role: [], // We'll set this separately
      });

      // Step 2: Reset all permissions to false first to ensure clean state
      rightsList.forEach((right) => {
        setValue(right, false, { shouldDirty: true, shouldTouch: true });
      });

      // Step 3: Set roles
      if (
        userDetails.sponsor_subrole_dto &&
        userDetails.sponsor_subrole_dto.length > 0
      ) {
        const roles = userDetails.sponsor_subrole_dto.map(
          (role) => role.sub_role
        );
        setValue("role", roles, { shouldDirty: true });
      }

      // Step 4: Set individual permissions from the API data
      // This must be done AFTER roles are set to prevent role-based overrides
      setTimeout(() => {
        if (
          userDetails.sponsor_rights_dto &&
          userDetails.sponsor_rights_dto.length > 0
        ) {
          // First ensure ALL permissions are explicitly set to false
          rightsList.forEach((right) => {
            setValue(right, false, { shouldDirty: true, shouldTouch: true });
          });

          // Then set only the ones from the API to true (excluding "Trial Experience Dashboard")
          userDetails.sponsor_rights_dto.forEach((right) => {
            if (
              right &&
              right.rights &&
              right.rights !== "Trial Experience Dashboard"
            ) {
              setValue(right.rights, true, {
                shouldDirty: true,
                shouldTouch: true,
              });
            }
          });
          // Chat Diligent access is stored separately; set checkbox from API
          setValue(
            "Access to Chat Diligent",
            userDetails.chat_diligent_access === true,
            { shouldDirty: true, shouldTouch: true }
          );

          // Explicitly ensure "Trial Experience Dashboard" is always false
          setValue("Trial Experience Dashboard", false, {
            shouldDirty: true,
            shouldTouch: true,
          });

          // Ensure My Vendors is selected by default for RFI Manager and Sponsor Admin
          const userRoles = userDetails.sponsor_subrole_dto?.map((role) => role.sub_role) || [];
          if (userRoles.includes("RFI Manager") || userRoles.includes("Sponsor Admin")) {
            setValue("My Vendors", true, { shouldDirty: true, shouldTouch: true });
          }
        }

        // Mark that we've loaded the initial user data
        setUserDataLoaded(true);
      }, 250); // Give sufficient time for role-based permissions to apply
    } else if (!userId) {
      reset({
        contact_preference: "Mobile_Phone",
        role: DEFAULT_NEW_SPONSOR_SUBROLES,
        is_active: true,
        job_title: "",
      });

      applySponsorPermissionsForRoles(setValue, DEFAULT_NEW_SPONSOR_SUBROLES, {
        canGrantChatDiligentAccess,
      });
    }
  }, [userDetails, reset, setValue, userDataLoaded, firstLoadComplete, userId]);

  useEffect(() => {
    if (userId && !userDataLoaded) return;
    applySponsorPermissionsForRoles(setValue, selectedRoles, {
      canGrantChatDiligentAccess,
      setValueOptions: { shouldDirty: true },
    });
  }, [selectedRoles, setValue, canGrantChatDiligentAccess, userId]);

  // Handle role selection
  const handleRoleChange = (e, role) => {
    if (isEditingSelf) return;
    const isChecked = e.target.checked;

    // Get current roles
    const currentRoles = getValues("role") || [];
    let newRoles = [];

    if (isChecked) {
      // If selecting Sponsor Admin, only allow it
      if (role === "Sponsor Admin") {
        newRoles = ["Sponsor Admin"];
      }
      // If selecting another role when Sponsor Admin is selected, switch to just this role
      else if (currentRoles.includes("Sponsor Admin")) {
        newRoles = [role];
      }
      // Otherwise add this role to existing selections
      else {
        newRoles = [...currentRoles, role];
      }
    } else {
      // If unchecking a role, just remove it
      newRoles = currentRoles.filter((r) => r !== role);
    }

    // Update the form with new role selections
    setValue("role", newRoles);
  };

  // Function to check if a role should be disabled
  const isRoleDisabled = (role) => {
    if (isEditingSelf) return true;
    const currentRoles = getValues("role") || [];
    if (role === "Sponsor Admin") {
      // Disable Sponsor Admin if any other role is selected
      return currentRoles.some((r) => r !== "Sponsor Admin" && r !== role);
    } else {
      // Disable other roles if Sponsor Admin is selected
      return currentRoles.includes("Sponsor Admin");
    }
  };

  // Modified onSubmit function to handle both create and update
  const onSubmit = (data) => {
    setIsLoading(true);

    // Concatenate country code and phone number
    const mobileNumber = data.phone_number_countryCode
      ? `${data.phone_number_countryCode}${data.phone_number}`
      : data.phone_number;
    const officeNumber = data.office_phone_number_countryCode
      ? `${data.office_phone_number_countryCode}${data.office_phone_number}`
      : data.office_phone_number;

    // Ensure we have selected roles
    if (!selectedRoles || selectedRoles.length === 0) {
      setIsSuccess(false);
      setMessage("Please select at least one role");
      setAlertOpen(true);
      setIsLoading(false);
      return;
    }

    const hyperComplyOrg = resolveUserHyperComplyOrg(
      sponsorDetails ? isVqaOnlyOrg(sponsorDetails) : ownOrgIsVqaOnly,
      decodeId
    );
    if (!hyperComplyOrg.ok) {
      setIsSuccess(false);
      setMessage(
        "Organization context is missing. Please reopen this page from the sponsor list."
      );
      setAlertOpen(true);
      setIsLoading(false);
      return;
    }

    // 14729: sponsor name from the sponsor record; the URL name param (seeded from the
    // JWT claim, which can be blank) is only a fallback.
    const sponsorName = sponsorDetails?.sponsor_name || name || "";

    // Get only the rights that are checked, excluding "Trial Experience Dashboard"
    let rights = rightsList.filter(
      (right) => data[right] === true && right !== "Trial Experience Dashboard"
    );
    // Only Lily (canGrantChatDiligentAccess) can grant "Access to Chat Diligent"; others preserve existing on edit
    if (!canGrantChatDiligentAccess) {
      rights = rights.filter((r) => r !== "Access to Chat Diligent");
      if (userId && userDetails?.chat_diligent_access === true) {
        rights.push("Access to Chat Diligent");
      }
    }

    // Prepare roles as array
    const rolesArray = Array.isArray(selectedRoles)
      ? selectedRoles
      : [selectedRoles];

    // Common data for both create and update
    const sponsorDataCreate = {
      email: data.email,
      phone_number: mobileNumber, // <-- use concatenated value
      office_phone_number: officeNumber, // <-- use concatenated value
      contact_preference: data.contact_preference,
      first_name: data.first_name,
      last_name: data.last_name,
      job_title: (data.job_title ?? "").trim() || null,
      sponsor_id: id,
      sponsor_name: sponsorName,
      is_active: userId ? data.is_active : true,
      hypercomply_org_id: hyperComplyOrg.hypercomplyOrgId,

      // Generate sponsor_subrole_dto with unique IDs
      sponsor_subrole_dto: rolesArray.map((role) => ({
        sponsor_subrole_id: uuidv4(),
        sub_role: role,
      })),

      // Generate sponsor_rights_dto with unique IDs
      sponsor_rights_dto: rights.map((right) => ({
        sponsor_rights_id: uuidv4(),
        rights: right,
      })),
    };
    const sponsorDataUpdate = {
      email: data.email,
      phone_number: mobileNumber, // <-- use concatenated value
      office_phone_number: officeNumber, // <-- use concatenated value
      contact_preference: data.contact_preference,
      first_name: data.first_name,
      last_name: data.last_name,
      job_title: (data.job_title ?? "").trim() || null,
      sponsor_id: data.sponsor_id,
      sponsor_name: sponsorName,
      is_active: userId ? data.is_active : true,
      hypercomply_org_id: hyperComplyOrg.hypercomplyOrgId,

      // Generate sponsor_subrole_dto with unique IDs
      sponsor_subrole_dto: rolesArray.map((role) => {
        // Try to find existing subrole with this role name
        const existingSubrole = userDetails?.sponsor_subrole_dto?.find(
          (sr) => sr.sub_role === role
        );

        // If found, keep the original ID, otherwise generate a new one
        return {
          sponsor_subrole_id: uuidv4(),
          sub_role: role,
        };
      }),

      // For update, keep original IDs when the right still exists, create new IDs only for newly added rights
      sponsor_rights_dto: rights.map((right) => {
        // Try to find existing right with this name
        const existingRight = userDetails?.sponsor_rights_dto?.find(
          (r) => r.rights === right
        );

        // If found, keep the original ID, otherwise generate a new one
        return {
          sponsor_rights_id: uuidv4(),
          rights: right,
        };
      }),
    };

    if (userId) {
      // Update existing user
      updateSponsor(
        { id: userId, userData: sponsorDataUpdate },
        {
          onSuccess: () => {
            // Force refetch after update succeeds
            setRefreshKey(Date.now());

            // Update states and show success message

            setIsLoading(false);
            setIsSuccess(true);
            setMessage("User updated successfully!");
            setAlertOpen(true);

            // Refetch after update
            refetch();
            // Navigate after a delay to ensure data is refreshed
            setTimeout(
              () => navigate(`/Sponsor/Team/Manage?sponsorId=${id}&name=${name}&tab=users`),
              2000
            );
          },
          onError: (error) => {
            setIsLoading(false);
            setIsSuccess(false);
            setMessage(
              error.response?.data?.message || "Failed to update user"
            );
            setAlertOpen(true);
          },
        }
      );
    } else {
      // Create new user
      addSponsor(sponsorDataCreate, {
        onSuccess: () => {
          setIsLoading(false);
          setIsSuccess(true);
          setMessage("User added successfully!");
          setAlertOpen(true);
          setTimeout(
            () => navigate(`/Sponsor/Team/Manage?sponsorId=${id}&name=${name}&tab=users`),
            2000
          );
        },
        onError: (error) => {
          setIsLoading(false);
          setIsSuccess(false);
          setMessage(getApiErrorMessage(error, "Failed to add user"));
          setAlertOpen(true);
        },
      });
    }
  };

  // Show loading state when fetching user details
  if (userId && (isLoadingUserDetails || isFetching)) {
    return (
      <div className="w-full flex justify-center px-4">
        <div className="w-full max-w-2xl bg-white shadow-sm rounded-lg p-8">
          <p className="text-center mt-4 text-gray-600">
            <FormSkeletonLoader />
          </p>
        </div>
      </div>
    );
  }

  // Checkboxes for permissions with central management
  const PermissionCheckbox = ({ rightItem, category }) => {
    // IMPORTANT: Use a more explicit way to get the form value
    // instead of relying on watch which might not be updating correctly
    const formValues = getValues();
    const currentValue = formValues[rightItem] === true;

    // Force component to re-render when permission values change
    const [, forceUpdate] = useState({});

    // Force re-render when permissions change
    useEffect(() => {
      const subscription = watch((value, { name }) => {
        if (name === rightItem || name === "role") {
          forceUpdate({});
        }
      });
      return () => subscription.unsubscribe();
    }, [watch, rightItem]);

    // Determine if this right belongs to any of the selected roles
    const isRoleSpecificRight = selectedRoles.some((role) => {
      if (role === "Sponsor Admin") return true; // Admin can modify all rights

      // For other roles, check if this right belongs to their category
      if (role === "RFI Manager" && category === "RFI Permissions") return true;
      if (role === "VQA Manager" && category === "Assessment Permissions")
        return true;

      return false;
    });

    // General permissions are always editable if any role is selected
    const isGeneralPermission =
      category === "General Permissions" && selectedRoles.length > 0;

    // Admin permissions are only editable by Sponsor Admin
    const isAdminPermission =
      category === "Admin Permissions" &&
      selectedRoles.includes("Sponsor Admin");

    // Determine if this checkbox should be editable
    const isEditable =
      !isEditingSelf &&
      (isRoleSpecificRight || isGeneralPermission || isAdminPermission);

    return (
      <div className="flex items-start">
        <div className="flex items-center h-5 pt-1">
          <input
            type="checkbox"
            id={`right-${rightItem}`}
            checked={currentValue}
            disabled={!isEditable}
            onChange={(e) => {
              setValue(rightItem, e.target.checked, {
                shouldDirty: true,
                shouldValidate: true,
              });
              // Force immediate UI update
              forceUpdate({});
            }}
            className={`h-4 w-4 rounded border-gray-300 ${
              isEditable
                ? "text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                : "text-gray-300 cursor-not-allowed"
            }`}
          />
        </div>
        <div className={`ml-3 text-sm ${!isEditable ? "opacity-50" : ""}`}>
          <label
            htmlFor={`right-${rightItem}`}
            className={`font-medium ${
              isEditable ? "text-gray-700" : "text-gray-400"
            }`}
          >
            {RIGHTS_DISPLAY_NAMES[rightItem] || rightItem}
          </label>
          <p className="text-xs text-gray-500 mt-1">
            {rightsDescriptions[rightItem] || "No description available"}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full flex justify-center px-4">
      <NotificationModal
        open={alertOpen}
        setOpen={setAlertOpen}
        desc={message}
        title={isSuccess ? "Success" : "Error"}
        error={!isSuccess}
      />

      {/* Form container with half-width and centered */}
      <div className="w-full max-w-2xl bg-white shadow-sm rounded-lg">
        <form onSubmit={handleSubmit(onSubmit)} className="w-full p-6">
          {/* Header */}
          <div className="pb-5 border-b border-gray-200">
            <h1 className="font-semibold text-xl">
              {userId ? "Edit Team Member" : "Add New Team Member"}
            </h1>
            <p className="text-sm text-gray-600">
              {userId
                ? "Update the information for this team member."
                : "Fill in the information below to create a new team member."}
            </p>
          </div>

          {/* User Information */}
          <div className="mt-6 space-y-6">
            {/* Email - Readonly when editing */}
            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Email"
                name="email"
                register={register}
                errors={errors}
                type="email"
                placeholder="user@example.com"
                disabled={!!userId} // Email is readonly in edit mode
              />
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-6">
              <FormInput
                label="First Name"
                name="first_name"
                register={register}
                errors={errors}
                placeholder="Enter first name"
                className="col-span-1"
              />
              <FormInput
                label="Last Name"
                name="last_name"
                register={register}
                errors={errors}
                placeholder="Enter last name"
                className="col-span-1"
              />
            </div>

            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Title"
                name="job_title"
                register={register}
                errors={errors}
                placeholder="e.g. VP of Clinical Operations"
              />
            </div>

            {/* Phone Numbers */}
            <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-6">
              {/* Mobile Phone Number */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-900">
                  Mobile Phone Number
                </label>
                <input
                  type="tel"
                  {...register("phone_number")}
                  className="ring-0 border-gray-300 rounded-md text-sm w-full px-3 py-2"
                  placeholder="e.g. 1-234-567-8900"
                  maxLength={14}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    e.target.value = formatted;
                  }}
                  onKeyDown={(e) => {
                    // Allow only digits, backspace, tab, left/right arrows, and dash
                    if (
                      ![8, 9, 37, 39].includes(e.keyCode) &&
                      !/\d|-/.test(e.key)
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
                {errors.phone_number && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.phone_number.message}
                  </p>
                )}
              </div>
              {/* Office Phone Number */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-900">
                  Office Phone Number
                </label>
                <input
                  type="tel"
                  {...register("office_phone_number")}
                  className="ring-0 border-gray-300 rounded-md text-sm w-full px-3 py-2"
                  placeholder="e.g. 1-234-567-8900"
                  maxLength={14}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    e.target.value = formatted;
                  }}
                  onKeyDown={(e) => {
                    if (
                      ![8, 9, 37, 39].includes(e.keyCode) &&
                      !/\d|-/.test(e.key)
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
                {errors.office_phone_number && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.office_phone_number.message}
                  </p>
                )}
              </div>
            </div>

            {/* Contact Preference */}
            <div className="border-b border-gray-200 pb-6">
              <RadioGroup
                label="Contact Preference"
                name="contact_preference"
                register={register}
                errors={errors}
              />
            </div>

            {/* Roles Section */}
            <div className="pb-5 border-b border-gray-200">
              <h2 className="font-semibold text-lg">User Roles</h2>
            </div>

            <div className="border-b border-gray-200 pb-6 ">
              {Object.keys(roleDefinitions).map((role) => (
                <div key={role} className=" rounded-md p-4">
                  <div
                    className={`flex items-center ${
                      isRoleDisabled(role) ? "opacity-50" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      id={`role-${role}`}
                      value={role}
                      checked={selectedRoles.includes(role)}
                      onChange={(e) => handleRoleChange(e, role)}
                      disabled={isRoleDisabled(role)}
                      className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                        isRoleDisabled(role)
                          ? "cursor-normal"
                          : "cursor-pointer"
                      }`}
                    />
                    <label htmlFor={`role-${role}`} className="ml-3 flex-1">
                      <span className="block text-sm font-medium text-gray-900">
                        {ROLE_DISPLAY_NAMES[role] || role}
                      </span>
                      <span className="block text-sm text-gray-500 mt-1">
                        {roleDefinitions[role].description || ""}
                      </span>
                    </label>
                  </div>
                </div>
              ))}
              {errors.role && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.role.message}
                </p>
              )}
              <input type="hidden" {...register("role")} />
            </div>

            {/* Permissions Section */}
            <div className="pb-5 border-b border-gray-200">
              <h2 className="font-semibold text-lg">User Permissions</h2>
              <p className="text-sm text-gray-600">
                Permissions will be automatically assigned based on selected
                roles.
              </p>
            </div>

            <div className="pb-6 space-y-6">
              {Object.entries(rightsCategories).map(
                ([category, categoryData]) => (
                  <div key={category} className="">
                    <div className="bg-gray-50 px-4 py-3">
                      <h3 className="text-sm font-medium text-gray-900">
                        {PERMISSION_CATEGORY_DISPLAY_NAMES[category] || category}
                      </h3>
                    </div>
                    <div className="p-4 space-y-4">
                      {categoryData.rights
                        .filter(
                          (rightItem) =>
                            rightItem !== "Trial Experience Dashboard" &&
                            (canGrantChatDiligentAccess ||
                              rightItem !== "Access to Chat Diligent")
                        )
                        .map((rightItem) => (
                          <PermissionCheckbox
                            key={rightItem}
                            rightItem={rightItem}
                            category={category}
                          />
                        ))}
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Hidden inputs for all rights */}
            {rightsList.map((right) => (
              <input
                type="hidden"
                key={`hidden-${right}`}
                {...register(right)}
              />
            ))}

            {/* Form Buttons */}
            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate(`/Sponsor/Team/Manage?sponsorId=${id}&name=${name}&tab=users`)}
                className="rounded-md px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-100 focus:outline-none"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUserRoleSelectionSaveDisabled(isLoading, selectedRoles)}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none ${
                  isUserRoleSelectionSaveDisabled(isLoading, selectedRoles)
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-500"
                }`}
              >
                {isLoading ? "Saving..." : userId ? "Save Changes" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTeamMember;
